/**
 * capstone.mjs — the smallest honest v0, and everything that is roadmap.
 *
 * FOUNDER, 2026-08-14: "ai101 labs next" → chapter 7.
 *
 * ── THE DISCIPLINE THE CHAPTER IS FOR ─────────────────────────────────────────────────────────
 * §6: "The v0 of any AI product is the smallest possible system that has all seven of Section 2 —
 * even if narrowly." And the warning that makes it a discipline rather than advice: "You will be
 * tempted to skip the small honest v0 in favour of a more ambitious demo. Every team is tempted.
 * Every team that yields to the temptation ships something brittle."
 *
 * So the instrument is a checklist you cannot cheat: tick the seven and you have a v0; tick an
 * ambitious extra and the lab tells you it is roadmap, by his own definition, not by mine.
 *
 * ── A TENSION IN HIS OWN TEXT, SURFACED RATHER THAN SMOOTHED ──────────────────────────────────
 * §2's seven include "Bounded agent loops". §6's v0 says "Zero agents. Zero tool calls beyond the
 * substrate's own operations."
 *
 * Read carelessly those contradict. Read with his own qualifier — "even if narrowly" — they do not:
 * you satisfy the bounded-agent-loop requirement in v0 by having no agents at all, which is the
 * narrowest possible satisfaction of it. An unbounded loop cannot occur where there is no loop.
 * The lab says exactly that rather than quietly dropping one of the seven or quietly allowing
 * agents into v0, and marks it as the lab's reading of his text.
 *
 * ── THE SYNTHESIS IS REAL AGAIN ───────────────────────────────────────────────────────────────
 * §5's five failure families each name a Day, and a lab was built for every one of those days
 * before this chapter was reached. Same as the Code101 capstone: the learner has watched each of
 * these fail with their own hands, so this chapter names them rather than re-explaining them.
 */

export const SOURCE_2 = 'CodeAI101 module 07 §2 "The seven things you ship that AI alone cannot"';
export const SOURCE_5 = 'CodeAI101 module 07 §5 "Failure mode synthesis — what can go wrong, across all of CodeAI101"';
export const SOURCE_6 = 'CodeAI101 module 07 §6 "v0 vs roadmap — what you ship now, what you defer"';

export const SMALLEST = 'The v0 of any AI product is the smallest possible system that has all seven of Section 2 — even if narrowly.';
export const TEMPTED = 'You will be tempted to skip the small honest v0 in favour of a more ambitious demo. Every team is tempted. Every team that yields to the temptation ships something brittle.';
export const SURVIVES = 'That is the smallest honest v0. It is also the version that survives an auditor\'s first question.';
export const EARNS_ITS_PLACE = 'Everything else — agents, multi-tool, multi-agent, fine-tuning — is roadmap. Each roadmap item earns its place by solving a real problem that v0 cannot.';
export const NOT_CLEVER_PROMPTS = 'Notice what is missing: clever prompts.';

/** §2's seven, in his order, each carrying the day it came from and the lab that taught it. */
export const SEVEN = Object.freeze([
  { n: 1, id: 'substrate', name: 'A declared substrate', day: 6, chapter: 6,
    quote: 'STATE, TRUST, SENSE, PROOF (Day 6). The system explains itself, by data, to compliance, auditors, and AI agents. Without this, your AI is a wrapper.' },
  { n: 2, id: 'retrieval', name: 'Curated retrieval over real data', day: 5, chapter: 5,
    quote: 'RAG with citation (Day 5). The AI answers from documents you can show, not from training it half-remembers.' },
  { n: 3, id: 'gates', name: 'Approval gates on tools', day: 3, chapter: 3,
    quote: 'three-tier discipline (Day 3). High-stakes tool calls are reviewed before they run. The AI cannot quietly delete production data.' },
  { n: 4, id: 'bounded', name: 'Bounded agent loops', day: 4, chapter: 4,
    quote: 'clear goals, attempt limits, cost limits, time limits, verifiable success conditions (Day 4). Agents that fail fail cheaply, not expensively.' },
  { n: 5, id: 'logs', name: 'Observability and logs', day: null, chapter: null,
    quote: 'every prompt, every retrieval, every tool call, every output, captured and queryable (carry-forward from Code101 Day 7). When a user reports a wrong answer, you can reconstruct exactly what happened.' },
  { n: 6, id: 'evals', name: 'Evaluation suites', day: null, chapter: null,
    quote: 'a body of test cases the system runs against on every change, scoring its answers against known-good references. Your system has a measurable accuracy that does not drift silently.' },
  { n: 7, id: 'fallbacks', name: 'Failure paths and fallbacks', day: null, chapter: null,
    quote: 'what the system does when the AI cannot answer, when the retrieval misses, when the tool is down. The system fails gracefully, not catastrophically.' },
]);

/** §6's v0, quoted line for line — the narrow form of each of the seven. */
export const V0 = Object.freeze([
  'One declared substrate (one record type, one operation, high-trust).',
  'Retrieval over a small, curated corpus.',
  'One AI-translator layer with strict system prompt and required citation.',
  'Zero agents. Zero tool calls beyond the substrate\'s own operations. The first version is human-driven prompts against a substrate-grounded RAG.',
  'Logs of every interaction.',
  'A small evaluation suite — twenty representative questions with known-good answers.',
  'A clear fallback when the AI cannot answer ("I do not have enough information; please rephrase, or contact a human").',
]);

/** The four he names as roadmap, verbatim from EARNS_ITS_PLACE. */
export const ROADMAP = Object.freeze([
  { id: 'agents', name: 'Agents' },
  { id: 'multitool', name: 'Multi-tool' },
  { id: 'multiagent', name: 'Multi-agent' },
  { id: 'finetune', name: 'Fine-tuning' },
]);

/**
 * THE TENSION, NAMED. §2 requires bounded agent loops; §6's v0 requires zero agents. His qualifier
 * "even if narrowly" resolves it, and the lab says how rather than dropping one side.
 */
export const BOUNDED_IN_V0 = 'v0 satisfies "bounded agent loops" by having no agents at all — the narrowest possible satisfaction of it, and the one §6 asks for by name: "Zero agents." An unbounded loop cannot happen where there is no loop. This is the lab\'s reading of "even if narrowly", not a separate rule.';

/** §5's five families, each naming its day and the lab where it was watched. */
export const FAMILIES = Object.freeze([
  { id: 'model', name: 'Model failures', day: 1, chapter: 1,
    quote: 'hallucination, stale knowledge, confident-but-wrong, refusing the right ask, over-confidence on edge cases. The fluent imitator failure surface.' },
  { id: 'prompt', name: 'Prompt and context failures', day: 2, chapter: 2,
    quote: 'context window exceeded (silently truncated), wrong system prompt, lost-in-the-middle, vague user prompt producing vague output, prompt injection (a malicious user input that rewrites the system\'s instructions).' },
  { id: 'tool', name: 'Tool failures', day: 3, chapter: 3,
    quote: 'wrong tool called, wrong parameters, side effects, infinite loops, permission escalation, irreversible actions taken on bad data.' },
  { id: 'agent', name: 'Agent failures', day: 4, chapter: 4,
    quote: 'drift from goal, runaway loops, cost blowout, goal completion claimed when not actually achieved, multi-agent miscommunication.' },
  { id: 'rag', name: 'RAG failures', day: 5, chapter: 5,
    quote: 'irrelevant retrieval, missing context across chunks, stale corpus, wrong source authority, hallucination on cited sources.' },
]);
export const UNDERNEATH = 'An AI system is a software system. Every Code101 failure mode is alive in it; CodeAI101 just adds new ones.';
export const THE_QUESTION = 'for the failure I am seeing, which family does it belong to?';

/**
 * Judge a proposed build. Not an opinion — his definition, applied.
 *
 * A v0 needs all seven. Anything from the roadmap list makes it not-a-v0, however complete the
 * seven are, because §6 defers those by name.
 */
export function judge(has = {}, extras = []) {
  const missing = SEVEN.filter((s) => !has[s.id]);
  const premature = ROADMAP.filter((r) => extras.includes(r.id));
  return {
    missing, premature,
    isV0: missing.length === 0 && premature.length === 0,
    verdict: missing.length === 0 && premature.length === 0 ? 'the smallest honest v0'
      : premature.length > 0 && missing.length === 0 ? 'past v0 — this is roadmap shipped early'
        : premature.length > 0 ? 'roadmap on an incomplete floor'
          : `not yet a v0 — ${missing.length} of the seven missing`,
  };
}
